import { computeMergedTheme, type BrandTheme } from '@owner/theme'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { FeatureFlags } from '@/plugins/featureFlags'

import { saveToRecentBrands } from '../public/recentBrands'
import { type Brand } from '../types'

import type { CoreBrand, CoreLocation } from './types'

export type CoreStoreBrand = CoreBrand & {
  locations: CoreLocation[]
  theme: BrandTheme
}

/**
 * Holds the source of truth for "core state" such as the active brand and location
 *
 * - Note: the store makes no assurances that data is populated (particularly activeBrand and activeBrandId)
 * so using this means you'll have to do those checks yourself if accessing the store directly
 *   - Composables such as useApplicationScope(), useActiveBrand(), and useActiveLocation() have some better
 *     safeguards in place for a more dev-friendly experience so feel free to use those if working within the BrandView
 */
export const useCoreStore = defineStore('core', () => {
  const activeBrandId = ref<string | undefined>(undefined)
  const activeBrand = ref<CoreStoreBrand | undefined>(undefined)
  const activeBrandLocations = computed(
    () => activeBrand.value?.locations ?? []
  )
  const activeLocationId = ref<string | undefined>(undefined)
  const activeLocation = computed(() => {
    let brandLocation: CoreLocation | undefined
    if (activeLocationId.value) {
      brandLocation = activeBrand.value?.locations.find(
        (loc) => loc.id === activeLocationId.value
      )
      if (!brandLocation) {
        throw new Error(
          `location ${activeLocationId.value} is not found in brand locations`
        )
      }
    }
    return brandLocation
  })

  const activeBrandMergedTheme = computed(() => {
    if (!activeBrand.value) {
      return undefined
    }
    return computeMergedTheme(activeBrand.value.theme)
  })

  const clearActiveBrand = () => {
    activeBrandId.value = undefined
    setActiveLocationId(undefined)
    setActiveBrand(undefined)
  }

  const setActiveBrandId = (brandId: string) => {
    // ensure we clear out brand/location data if the brandId and
    // the activeBrand don't match
    if (activeBrand.value?.id !== brandId) {
      setActiveLocationId(undefined)
      setActiveBrand(undefined)
    }
    activeBrandId.value = brandId
    FeatureFlags.setContextField('brandId', brandId)
  }

  const setActiveLocationId = (locationId: string | undefined) => {
    activeLocationId.value = locationId
    FeatureFlags.setContextField('locationId', locationId ?? '')
  }

  const setActiveBrand = (brand: CoreStoreBrand | undefined) => {
    // clear any active location data
    activeLocationId.value = undefined

    // set activeBrand
    activeBrand.value = brand
    if (brand) {
      saveToRecentBrands(brand)
    }

    // if brand only has 1 location, set it as the active location
    if (brand?.locations.length === 1) {
      setActiveLocationId((brand.locations[0] as CoreLocation).id)
    }
  }

  const updateActiveBrandData = (brand: Brand | CoreBrand) => {
    if (!activeBrand.value) {
      throw new Error(
        'cannot update activeBrand data when there is no active brand'
      )
    }

    if (activeBrand.value.id !== brand.id) {
      throw new Error(
        'current activeBrand does not match the brand data being passed in'
      )
    }

    activeBrand.value = {
      ...activeBrand.value,
      ...brand,
    }
  }

  const updateLocationData = (
    locationId: string,
    update: Partial<CoreLocation>
  ) => {
    if (!activeBrand.value) {
      throw new Error(
        'cannot update location data when there is no active brand'
      )
    }
    const brandLocation = activeBrand.value.locations.find(
      (loc) => loc.id === locationId
    )
    if (!brandLocation) {
      throw new Error(
        'cannot update location that is not on currently-active brand'
      )
    }

    const newBrandLocations = activeBrand.value.locations.map((loc) => {
      if (loc.id === locationId) {
        return { ...loc, ...update }
      }
      return loc
    })

    activeBrand.value = {
      ...activeBrand.value,
      locations: newBrandLocations,
    }
  }

  const addBrandLocation = (location: CoreLocation) => {
    if (!activeBrand.value) {
      throw new Error('cannot add brand location when there is no active brand')
    }
    activeBrand.value.locations.push(location)
  }

  return {
    activeBrand,
    activeBrandId,
    activeBrandLocations,
    activeBrandMergedTheme,
    activeLocation,
    activeLocationId,
    addBrandLocation,
    clearActiveBrand,
    setActiveBrand,
    setActiveBrandId,
    setActiveLocationId,
    updateActiveBrandData,
    updateLocationData,
  }
})
